import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';

const COLORS = ['#0E7490', '#14B8A6', '#CA8A04', '#7C3AED', '#DC2626', '#2563EB', '#059669', '#D97706', '#7C3AED', '#EC4899'];

export function BarChartWidget({ data, title, xKey = 'label', yKey = 'count' }) {
  if (!data?.length) return <EmptyChart title={title} />;
  return (
    <div data-testid="bar-chart">
      {title && <h4 className="text-sm font-medium text-[#0A2342] mb-2">{title}</h4>}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748B' }} />
          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
          <Bar dataKey={yKey} fill="#0E7490" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DonutChartWidget({ data, title, nameKey = 'label', valueKey = 'count' }) {
  if (!data?.length) return <EmptyChart title={title} />;
  const total = data.reduce((s, d) => s + (d[valueKey] || 0), 0);
  return (
    <div data-testid="donut-chart">
      {title && <h4 className="text-sm font-medium text-[#0A2342] mb-2">{title}</h4>}
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey={valueKey} nameKey={nameKey} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: '#94A3B8' }} >
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => [v, '']} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center text-xs text-[#64748B]">Total: {total}</div>
    </div>
  );
}

export function TrendlineWidget({ data, title, xKey = 'date', yKey = 'value', label }) {
  if (!data?.length) return <EmptyChart title={title} />;
  return (
    <div data-testid="trendline-chart">
      {title && <h4 className="text-sm font-medium text-[#0A2342] mb-2">{title}</h4>}
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0E7490" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#0E7490" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: '#64748B' }} />
          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
          <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
          <Area type="monotone" dataKey={yKey} stroke="#0E7490" fill="url(#trendGrad)" strokeWidth={2} name={label || yKey} dot={{ r: 3, fill: '#0E7490' }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HeatmapWidget({ data, title, rows, columns }) {
  if (!rows?.length || !columns?.length) return <EmptyChart title={title} />;
  const maxVal = Math.max(...Object.values(data || {}).map(v => (typeof v === 'number' ? v : v ? 1 : 0)), 1);
  const getColor = (val) => {
    if (!val) return '#F8FAFC';
    const intensity = Math.min((typeof val === 'number' ? val : 1) / maxVal, 1);
    return `rgba(14, 116, 144, ${0.1 + intensity * 0.8})`;
  };
  return (
    <div data-testid="heatmap-chart">
      {title && <h4 className="text-sm font-medium text-[#0A2342] mb-2">{title}</h4>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr><th className="p-1"></th>
            {columns.map((c, i) => <th key={i} className="p-1 text-[#64748B] font-medium text-center whitespace-nowrap">{c}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                <td className="p-1 text-[#64748B] font-medium whitespace-nowrap pr-2">{r}</td>
                {columns.map((c, ci) => {
                  const val = data?.[`${ri}_${ci}`];
                  return <td key={ci} className="p-1 text-center" style={{ backgroundColor: getColor(val), borderRadius: 4 }}>
                    <span className="text-[10px]">{val === true ? 'Y' : val === false ? '' : (val || '')}</span>
                  </td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ScorecardWidget({ label, value, subtitle, trend, color = '#0E7490' }) {
  return (
    <div className="p-4 rounded-xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC]" data-testid="scorecard">
      <div className="text-xs text-[#64748B] mb-1">{label}</div>
      <div className="text-3xl font-bold" style={{ color }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {subtitle && <div className="text-[10px] text-[#94A3B8] mt-1">{subtitle}</div>}
      {trend !== undefined && (
        <div className={`text-xs mt-1 ${trend >= 0 ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
          {trend >= 0 ? '+' : ''}{trend}%
        </div>
      )}
    </div>
  );
}

export function PivotTableWidget({ data, title, columns }) {
  if (!data?.length || !columns?.length) return <EmptyChart title={title} />;
  // Group by first column, aggregate counts
  const groupCol = columns[0];
  const valCols = columns.slice(1);
  const groups = {};
  data.forEach(row => {
    const key = row[groupCol?.key] || 'Other';
    if (!groups[key]) groups[key] = { _label: key, _count: 0 };
    groups[key]._count++;
    valCols.forEach(vc => {
      const val = row[vc.key];
      if (val !== undefined && val !== '') {
        if (!isNaN(Number(val))) {
          groups[key][vc.label] = (groups[key][vc.label] || 0) + Number(val);
          groups[key][`${vc.label}_count`] = (groups[key][`${vc.label}_count`] || 0) + 1;
        } else {
          groups[key][vc.label] = (groups[key][vc.label] || 0) + 1;
        }
      }
    });
  });
  const pivotRows = Object.values(groups).sort((a, b) => b._count - a._count);

  return (
    <div data-testid="pivot-table">
      {title && <h4 className="text-sm font-medium text-[#0A2342] mb-2">{title}</h4>}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead><tr className="bg-[#F1F5F9]">
            <th className="px-3 py-2 text-left font-medium text-[#64748B]">{groupCol?.label || 'Group'}</th>
            <th className="px-3 py-2 text-right font-medium text-[#64748B]">Count</th>
            {valCols.map(vc => <th key={vc.key} className="px-3 py-2 text-right font-medium text-[#64748B]">{vc.label}</th>)}
          </tr></thead>
          <tbody>
            {pivotRows.map((row, i) => (
              <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                <td className="px-3 py-2 font-medium text-[#0A2342]">{row._label}</td>
                <td className="px-3 py-2 text-right text-[#64748B]">{row._count}</td>
                {valCols.map(vc => {
                  const v = row[vc.label];
                  const cnt = row[`${vc.label}_count`];
                  return <td key={vc.key} className="px-3 py-2 text-right text-[#0A2342]">
                    {v !== undefined ? (cnt ? `${Math.round(v / cnt * 10) / 10} avg (${v})` : v) : '—'}
                  </td>;
                })}
              </tr>
            ))}
            <tr className="border-t-2 border-[#0E7490] bg-[#F0FDFA] font-bold">
              <td className="px-3 py-2 text-[#0E7490]">Total</td>
              <td className="px-3 py-2 text-right text-[#0E7490]">{pivotRows.reduce((s, r) => s + r._count, 0)}</td>
              {valCols.map(vc => <td key={vc.key} className="px-3 py-2 text-right text-[#0E7490]">{pivotRows.reduce((s, r) => s + (r[vc.label] || 0), 0)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyChart({ title }) {
  return (
    <div className="text-center py-8 text-[#94A3B8]">
      {title && <h4 className="text-sm font-medium mb-2">{title}</h4>}
      <p className="text-xs">No data available</p>
    </div>
  );
}
